import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, Package, Truck, Receipt, FileMinus, UserSquare2 } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDate, fmtNum } from "../../lib/format";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const OPEN = {
  orders: (s) => !["LIVREE", "FACTUREE", "PAYEE", "ANNULEE"].includes(s),
  delivery_notes: (s) => !["SIGNE", "ANNULE"].includes(s),
  invoices: (s) => ["ENVOYEE", "PARTIELLE", "EN_RETARD"].includes(s),
  credit_notes: (s) => ["ENVOYEE", "EXECUTEE"].includes(s),
};
const CANCELLED = ["ANNULEE", "ANNULE"];

const TABS = [
  { key: "orders", label: "Commandes", icon: Package, path: "/commandes-clients", date: "created_at" },
  { key: "delivery_notes", label: "BL", icon: Truck, path: "/bons-livraison", date: "delivery_date" },
  { key: "invoices", label: "Factures", icon: Receipt, path: "/factures", date: "issue_date" },
  { key: "credit_notes", label: "Avoirs", icon: FileMinus, path: "/notes-de-credit", date: "issue_date" },
];

const statusTone = (s) => {
  if (["PAYEE", "SIGNE", "LIVRE", "LIVREE", "EXECUTEE"].includes(s)) return "bg-[#EAF3EE] text-[#1F4E3D] border-[#C6DFCC]";
  if (s === "EN_RETARD") return "bg-[#FBE5E0] text-[#A04E2C] border-[#E8C7BD]";
  if (s === "BROUILLON") return "bg-[#F0EBD8] text-[#6B6B4E] border-[#D4C97A]";
  if (CANCELLED.includes(s)) return "bg-[#EEE] text-[#6B6E6B] border-[#C5C6C1] line-through";
  return "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D5A9]";
};

const Amount = ({ tab, d }) => {
  if (tab === "credit_notes") return <span className="text-[#8B3A1F]">− {fmtNum(d.total_ttc)}</span>;
  if (tab === "invoices" && d.amount_due > 0) {
    return <span>{fmtNum(d.total_ttc)}<span className="block text-[10px] text-[#A04E2C]">reste {fmtNum(d.amount_due)}</span></span>;
  }
  return d.total_ttc != null ? fmtNum(d.total_ttc) : "—";
};

const Extra = ({ tab, d }) => {
  if (tab === "orders") return <>{d.items_count} art.{d.is_web && " · web"}{d.bl_id && " · BL"}{d.invoice_id && " · facturée"}</>;
  if (tab === "delivery_notes") return <>{d.invoice_id ? "facturé" : "non facturé"}</>;
  if (tab === "invoices") return <>échéance {fmtDate(d.due_date)}</>;
  return <>{d.source_invoice_reference ? `facture ${d.source_invoice_reference}` : ""}</>;
};

const DocTable = ({ tab, rows }) => {
  const meta = TABS.find((t) => t.key === tab);
  if (!rows.length) return <p className="text-sm text-[#575A57] py-6 text-center" data-testid={`account-empty-${tab}`}>Aucun document.</p>;
  return (
    <div className="border border-[#E5E5E0] rounded overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#FAFAF7]">
            <TableHead>Référence</TableHead><TableHead>Date</TableHead><TableHead>Détail</TableHead>
            <TableHead className="text-right">Montant CHF</TableHead><TableHead className="text-center">Statut</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((d) => (
            <TableRow key={d.id} data-testid={`account-row-${tab}-${d.id}`}>
              <TableCell className="font-mono font-semibold">
                <Link to={`${meta.path}/${d.id}`} className="text-[#1F4E3D] hover:underline" data-testid={`account-open-${d.id}`}>{d.reference || "(sans n°)"}</Link>
              </TableCell>
              <TableCell className="text-xs whitespace-nowrap">{fmtDate(d[meta.date] || d.created_at)}</TableCell>
              <TableCell className="text-xs text-[#575A57]"><Extra tab={tab} d={d} /></TableCell>
              <TableCell className="text-right font-mono text-sm"><Amount tab={tab} d={d} /></TableCell>
              <TableCell className="text-center"><Badge className={`${statusTone(d.status)} text-[10px] font-mono`}>{d.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const ResellerAccountPanel = ({ resellerId }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("active");
  const { hash } = useLocation();

  useEffect(() => {
    api.get(`/resellers/${resellerId}/account`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [resellerId]);
  useEffect(() => {
    if (hash === "#compte" && data) document.getElementById("compte")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, data]);

  const filtered = useMemo(() => {
    if (!data) return {};
    const out = {};
    TABS.forEach(({ key }) => {
      out[key] = (data[key] || []).filter((d) => {
        if (filter === "all") return true;
        if (filter === "open") return OPEN[key](d.status);
        if (filter === "draft") return d.status === "BROUILLON";
        return !CANCELLED.includes(d.status);
      });
    });
    return out;
  }, [data, filter]);

  return (
    <section id="compte" className="bg-white border border-[#E5E5E0] rounded-lg p-5 lg:col-span-3 scroll-mt-20" data-testid="reseller-account-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-heading text-base flex items-center gap-2"><UserSquare2 className="w-4 h-4 text-[#1F4E3D]" /> Compte client</h3>
        {data && (
          <div className="flex items-center gap-4">
            <div className="text-right" data-testid="account-total-due">
              <div className="text-[10px] uppercase tracking-wider text-[#575A57]">Reste dû (factures ouvertes)</div>
              <div className={`font-mono font-bold text-lg ${data.total_due > 0 ? "text-[#A04E2C]" : "text-[#1F4E3D]"}`}>{fmtNum(data.total_due)} CHF</div>
              {data.overdue_count > 0 && <div className="text-[10px] text-[#A04E2C]">{data.overdue_count} en retard</div>}
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="account-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Tous (sans annulés)</SelectItem>
                <SelectItem value="open">Ouverts / à traiter</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="all">Tout, annulés compris</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {data === null && <div className="py-6 text-center text-[#575A57]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>}
      {data === false && <p className="text-sm text-red-600">Impossible de charger le compte.</p>}
      {data && (
        <Tabs defaultValue="invoices">
          <TabsList className="flex-wrap h-auto">
            {TABS.map(({ key, label, icon: Icon }) => (
              <TabsTrigger key={key} value={key} data-testid={`account-tab-${key}`} className="gap-1.5">
                <Icon className="w-3.5 h-3.5" /> {label}
                <span className="ml-1 text-[10px] px-1.5 rounded-full bg-[#F0F0EB] text-[#575A57]" data-testid={`account-count-${key}`}>{filtered[key].length}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(({ key }) => (
            <TabsContent key={key} value={key} className="mt-3"><DocTable tab={key} rows={filtered[key]} /></TabsContent>
          ))}
        </Tabs>
      )}
    </section>
  );
};

export default ResellerAccountPanel;
